// Redesigned package/option card. Same props + click behavior as
// PackageDisplay -- only the render is new. Structure and CSS classes mirror
// the senior-sitting session card (app/assets/stylesheets/seniors.scss's
// `.seniors-package-card` system, wired up in
// _pages/appointment-session-selection). Keeping both files means the
// existing PackageDisplay ships unchanged for every school that isn't
// previewing the new layout.

import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router'
import cn from 'classnames'
import axios from 'axios'
import { CustomerContext } from '../../contexts/CustomerContext'
import Image from '../Image'
import defaultBackground from '../../assets/Package_Blue.png'
import AbsolutePosImgs from '../_pages/AbsolutePosImgs/AbsolutePosImgs'
import discountedPrice, { money } from '../../utils/offerPricing'
import { routes } from '../../routes'
import useBuyAllOption from '../../hooks/useBuyAllOption'
import useLocalStorage from '../../hooks/useLocalStorage'
import useAddons from '../../hooks/useAddons'
import usePricelist from '../../hooks/usePricelist'
import { isBuyAllDigital } from '../../utils/buyAllDigital'
import { collapseItems, isDownloadItem, hasPhysicalItem } from './packageItems'
import { freeMagnetsBadge, freeMagnetsRow, magnetsOptionLabel } from './packageCardOffer'
import { afterElementaryBuildRoute } from '../_pages/_ElementaryMultiPose/FreeMagnets/freeMagnetsVisibility'
import { freeMagnetsStatus } from '../../utils/freeMagnets'
import QueryKeys from '../../utils/QueryKeys'
import { useQueryClient } from '@tanstack/react-query'
import styles from './PackageDisplayV2.module.sass'

const priceParts = (val) => {
  const num = Number(val) || 0
  const dollars = Math.floor(num)
  const cents = String(Math.round((num - dollars) * 100)).padStart(2, '0')
  return { dollars, cents }
}

// Light-gray circle + white check. One per line item in the package
// contents, mirroring the icon pattern used by the senior-sitting card
// (SeniorSessionCard `.seniors-package-card__feature-icon`). Colors are
// intentionally hardcoded here so the icon reads the same regardless of the
// text color the .seniors-package-card--featured variant flips to.
const CheckIcon = () => (
  <svg
    className="package-v2-check"
    width="16" height="16" viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="12" fill="#b8dbec" />
    <path
      d="M6.5 12.25l3.5 3.5 7.5-7.5"
      stroke="#fff" strokeWidth="2.5" fill="none"
      strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
)

// Download variant: white circle background + #008fcf (Shoob brand blue)
// down-arrow-into-tray glyph. Draws attention to digital items so they
// read differently from print items at a glance. Glyph is deliberately
// scaled up (viewBox 0..20) so it visually matches the check icon's
// footprint -- with the white circle blending into the page background,
// the glyph itself has to be as big as the check's gray circle to read
// at the same size.
const DownloadIcon = () => (
  <svg
    className="package-v2-download"
    width="16" height="16" viewBox="0 0 20 20"
    aria-hidden="true"
  >
    <path
      d="M10 3v9m0 0l-4-4m4 4l4-4M4 17h12"
      stroke="#b8dbec" strokeWidth="2.4" fill="none"
      strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
)

const iconForItem = (label) => (
  isDownloadItem(label) ? <DownloadIcon /> : <CheckIcon />
)

// Digital-perks block that renders under the item list on All-Digital
// (option.digitalImagesOnly) cards only. Tabler-style outline glyphs
// inlined so we don't add an icon dependency for three uses; sized to
// match the checklist row's ~16px icon footprint. Colors match the
// Shoob brand blue (#008fcf) already used on the download glyph.
const DigitalPerksBullet = ({ icon, heading, body }) => (
  <li className="package-v2-digital-perk">
    <span className="package-v2-digital-perk__icon" aria-hidden="true">{icon}</span>
    <span className="package-v2-digital-perk__text">
      <strong>{heading}</strong>
      {body}
    </span>
  </li>
)

const DigitalPerksList = () => (
  <ul className="package-v2-digital-perks">
    <DigitalPerksBullet
      icon={(
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#008fcf" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
          <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
        </svg>
      )}
      heading="High-resolution files"
      body=""
    />
    <DigitalPerksBullet
      icon={(
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#008fcf" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2" />
          <path d="M17 9V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
          <rect x="7" y="13" width="10" height="8" rx="2" />
        </svg>
      )}
      heading="Full printing rights"
      body=""
    />
    <DigitalPerksBullet
      icon={(
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#008fcf" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6.34 15.34A5 5 0 0 1 8 5.5a6 6 0 0 1 11.32 2A4.5 4.5 0 0 1 17.5 16H7a3.66 3.66 0 0 1-.66-.66z" />
          <path d="M12 12v6" />
          <path d="M9 15l3-3 3 3" />
        </svg>
      )}
      heading="Ultimate Convenience"
      body=""
    />
  </ul>
)

const PackageDisplayV2 = ({
  multiPoseHandler, option, price, packageId, isElementary,
  preorderPose, offer, compact, imageTypes, allPosesAreJpg,
}) => {
  const history = useHistory()
  const { customer } = useContext(CustomerContext)
  const queryClient = useQueryClient()

  const { data: pricelist } = usePricelist(packageId)
  const { data: buyAllOption, isLoading: isLoadingBuyAll } = useBuyAllOption(packageId, customer.currentSchool.id)
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`)
  const { data: addons } = useAddons(isElementary)

  const rawPrice = price?.price
  const discounted = discountedPrice(rawPrice, offer)
  const displayPrice = discounted != null ? discounted : rawPrice
  const { dollars, cents } = priceParts(displayPrice)

  // Package contents -- one <li> per row in the admin's "Items for Package
  // Type" (image_types). Filter blank names (admin blank rows). Repeats
  // collapse into a single line with the summed count (e.g. three
  // "(1) High-Resolution Download" rows -> "(3) High-Resolution Downloads").
  // If nothing is set up in image_types, fall back to the free-text
  // option.description paragraph so nothing renders empty.
  //
  // Computed before the savings math because whether this option ships is
  // read off the item list -- see shipsPhysicalItems below.
  const optionItems = collapseItems(
    (imageTypes || [])
      .map((t) => (t && t.name) ? t.name.trim() : '')
      .filter((s) => s.length > 0)
  )

  // The free-magnets tier (issue #5218): a badge, an appended checklist row and
  // a magnet slot in the composite, all keyed off the ONE interpreter
  // (grantsFreeMagnets via packageCardOffer). Option contents/prices are
  // untouched; on day 46 all three vanish with the offer. The row is appended
  // AFTER the contents so shipping detection (hasPhysicalItem) below still
  // reads the option's own rows -- the magnets ship, but a digital-only card
  // must keep reading as digital for its "Free Shipping" maths.
  const magnetBadge = freeMagnetsBadge(offer)
  const magnetRow = freeMagnetsRow(offer)
  const items = magnetRow ? [...optionItems, magnetRow] : optionItems
  // The a-la-carte Magnets option reads "FREE with any package" while live.
  const magnetsLabel = magnetsOptionLabel(option, offer)

  // Total savings the customer sees on this card = the price discount
  // (rawPrice - discounted) PLUS the shipping cost if the coupon includes
  // free shipping. Mirrors the Rails Coupon#free_shipping? substring test
  // (Coupon model line 290) so the frontend and backend agree on which
  // coupons waive shipping.
  //
  // Whether there IS shipping to waive comes from the package CONTENTS
  // (hasPhysicalItem), not from the digitalImagesOnly flag -- see the note
  // on that helper for why the flag is the wrong signal.
  //
  // Keying this on digitalImagesOnly was wrong for a MIXED package. The
  // elementary "Star Student" option bundles prints AND every digital image
  // and is flagged digitalImagesOnly, so the card hid "+ Free Shipping" and
  // under-reported "Save $X" by the shipping amount -- while checkout went
  // on charging (and the coupon went on waiving) shipping, because the
  // sheets are physical.
  //
  // With no image_types configured there is nothing to inspect, so fall
  // back to the flag rather than silently assuming a card never ships.
  const priceSavings = discounted != null && rawPrice != null
    ? Math.max(rawPrice - discounted, 0)
    : 0
  const isAllDigital = isBuyAllDigital(option)
  const shipsPhysicalItems = hasPhysicalItem(optionItems, !isAllDigital)
  const couponFreeShipping = !!offer?.couponType && offer.couponType.includes('FREE_SHIPPING')
  const includesFreeShipping = couponFreeShipping && shipsPhysicalItems
  const shippingSavings = includesFreeShipping ? Number(pricelist?.shippingPrice) || 0 : 0
  const savings = priceSavings + shippingSavings
  // Whether the savings subline can name a percentage. Read four times by
  // the subline below, so resolved once here.
  const hasPercentOff = Number(offer?.value) > 0 && !!offer?.couponType?.includes('PERCENTAGE')

  // The three digital selling-point bullets are driven by their OWN admin
  // checkbox ("Show digital perks bullets" -> options.show_digital_perks),
  // which does nothing but show them. They used to render off
  // digitalImagesOnly, which is a BEHAVIORAL flag: it makes the elementary
  // builder throw away the sheet list and build one digital sheet per
  // favorited pose. A mixed prints-and-digital package wants the bullets and
  // must not have that behavior, so the card no longer conflates the two.
  // The column was backfilled from digital_images_only, so cards showing the
  // bullets before this split kept them.
  const showDigitalPerks = !!option.showDigitalPerks

  // "Most Popular" badge + featured card treatment are driven by the
  // Most Popular admin checkbox on the pricelist package option
  // (options.most_popular; toggled in admin/packages/_option_fields.html.erb).
  const isFeatured = !!option.mostPopular
  const isBestValue = !!option.bestValue

  const renderItems = () => {
    if (items.length > 0) {
      // Cards with 5+ items compress into 2 columns so the checklist fits
      // in the reserved Features footprint. Shorter lists stay 1-col.
      // Download items span full width in either mode.
      const multiCol = items.length > 4 && !compact
      return (
        <ul className={cn('seniors-package-card__features', styles.itemList, {
          [styles.itemListMultiCol]: multiCol,
          // Most Popular gets a taller Features block (4 reserved rows)
          // even if its own list only fills 3 -- top row sits empty and
          // the extra height makes this card stand out visually.
          [styles.itemListTallMostPopular]: multiCol && isFeatured,
          // Tighten the gap under the list when a divider-topped perks block
          // follows immediately below.
          [styles.itemListDigital]: showDigitalPerks,
        })}>
          {items.map((label, i) => (
            // has-feature-icon suppresses the global .seniors-package-card__features
            // li::before blue-dot bullet (seniors.scss:840-854).
            // Download items get bolded so they stand out from print items.
            <li
              key={i}
              className={cn('has-feature-icon', styles.itemRow, {
                [styles.itemRowDownload]: isDownloadItem(label),
              })}
            >
              {iconForItem(label)}
              <span>{label}</span>
            </li>
          ))}
        </ul>
      )
    }
    // Skip the description fallback when the perks block is showing: it
    // (rendered right below by the caller) already carries the customer-
    // facing messaging, and duplicating the option.description above it
    // would push the price row down out of alignment with sibling cards.
    if (option.description && !showDigitalPerks) {
      return (
        <p className={cn('seniors-package-card__features', styles.description)}>
          {option.description}
        </p>
      )
    }
    return null
  }

  const handleContinue = async () => {
    if (option.id === buyAllOption?.id) {
      const response = await axios.post(
        `/carts/${customer?.cart?.id}/buy_all`,
        {
          package_id: packageId,
          option_id: buyAllOption.id,
          student_id: customer.currentStudent.id,
          season: currentSeason,
        }
      )
      const orderPackageId = response.data?.order_package_id
      // Buy All Digital qualifies for the free magnets (issue #5218): detour
      // through the magnet page on the way to wherever this was going, deciding
      // from the FRESH flags the /buy_all response carries (never the cached cart).
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] })
      const status = await freeMagnetsStatus({ data: response.data, cartId: customer?.cart?.id })
      const viaMagnets = (next) => afterElementaryBuildRoute({
        next, orderPackageId, offer, status, allJpg: !!allPosesAreJpg,
      })
      if ([653, 654, 656].includes(packageId)) return history.push(viaMagnets(routes.confirm(orderPackageId)))
      if (addons.length === 0 || !orderPackageId) return history.push(viaMagnets(routes.review()))
      return history.push(viaMagnets(routes.confirm(orderPackageId)))
    }
    multiPoseHandler(option.id)
  }

  if (!(option && price)) return null
  if (isLoadingBuyAll) return null

  const hasRealThumbnail = option.imageUrl && !option.imageUrl.includes('missing')
  const imgSrc = hasRealThumbnail ? option.imageUrl : defaultBackground

  // Compact variant — matches the senior "extras" row: no image, tighter
  // spacing, and the Select button sits inline with the price. Deliberately
  // less prominent than the top 3 cards so the primary options still lead.
  if (compact) {
    return (
      <div className={cn('seniors-package-card', styles.card, styles.compactCard)}>
        <h3 className={cn('seniors-package-card__name', styles.compactName)}>
          {option.name}
        </h3>
        {(magnetsLabel || option.tagline) && (
          <p className={cn(styles.compactTagline, { [styles.freeLabel]: !!magnetsLabel })}>
            {magnetsLabel || option.tagline}
          </p>
        )}
        {renderItems()}
        <div className={styles.compactPriceRow}>
          {discounted != null && rawPrice != null && (
            <span className={styles.compactStrikePrice}>{money(rawPrice)}</span>
          )}
          <p className={cn('seniors-package-card__price', styles.compactPrice)}>
            <span className="seniors-package-card__currency">$</span>{dollars}
            <span className="seniors-package-card__cents">.{cents}</span>
          </p>
          <button
            type="button"
            className={cn('seniors-btn', 'seniors-btn--outline', styles.selectBtn)}
            onClick={handleContinue}
          >
            Select
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('seniors-package-card', styles.card, {
        'seniors-package-card--featured': isFeatured,
        [styles.mostPopularCard]: isFeatured,
      })}
    >
      <div className={styles.imageWrap}>
        {/* Image + AbsolutePosImgs live inside their own overflow: hidden
            wrapper so a mis-sized overlay is clipped at the thumbnail edge
            (matches V1's .upperContainer behavior). The badge sits OUTSIDE
            this wrapper -- it's positioned at top: -12px on the card art and
            would get clipped away if it lived inside the overflow: hidden. */}
        <div className={styles.imageClip}>
          <Image src={imgSrc} alt={option.name} classes={styles.image} />
          {/* AbsolutePosImgs positions student photos onto the real thumbnail
              via coordinates baked in for each thumbnail. When there's no real
              thumbnail (option has no imageUrl or a "missing" placeholder),
              the overlay has nothing to anchor to and stacks photos into a
              grid of tiled duplicates -- hide it in that case. */}
          {hasRealThumbnail && !preorderPose && !pricelist?.hideCss
            && AbsolutePosImgs(customer, packageId, option)}
        </div>
        {(isBestValue || isFeatured) && (
          <div className={cn('seniors-package-card__badge', styles.badge)}>
            {isFeatured ? 'Most Popular' : 'Best Value'}
          </div>
        )}
        {magnetBadge && (
          <div className={styles.magnetBadge}>{magnetBadge}</div>
        )}
      </div>

      <h3 className={cn('seniors-package-card__name', styles.name)}>
        {option.name}
      </h3>

      {/* Always render the promo slot -- even when a card has no tagline --
          so the Features section starts at the same Y position on every
          card. Non-breaking space keeps the line's height. */}
      <p className={cn(styles.tagline, { [styles.freeLabel]: !!magnetsLabel })}>
        {magnetsLabel || option.tagline || ' '}
      </p>

      {renderItems()}

      {showDigitalPerks && <DigitalPerksList />}

      <div className={styles.priceRow}>
        {discounted != null && rawPrice != null && (
          <span className={styles.strikePrice}>{money(rawPrice)}</span>
        )}
        <p className={cn('seniors-package-card__price', styles.price)}>
          {money(displayPrice)}
        </p>
        {savings > 0 && (
          <div className={styles.offerSavings}>
            Save {money(savings)}
            {hasPercentOff || includesFreeShipping ? (
              <>
                {' ('}
                {hasPercentOff && `${Number(offer.value)}%`}
                {/* With no "+ Free Shipping" tail to pair with, the percent
                    reads as a bare number -- append " Off" so it's a phrase.
                    Gated on the tail itself rather than on digitalImagesOnly,
                    which is what produced "(20% Off + Free Shipping)" on a
                    mixed prints-and-digital card. */}
                {hasPercentOff && !includesFreeShipping && ' Off'}
                {hasPercentOff && includesFreeShipping && ' + '}
                {includesFreeShipping && 'Free Shipping'}
                {')'}
              </>
            ) : null}
          </div>
        )}
      </div>

      <div className={styles.actionRow}>
        <button
          type="button"
          className={cn('seniors-btn', 'seniors-btn--primary', styles.selectBtn)}
          onClick={handleContinue}
        >
          Select
        </button>
      </div>
    </div>
  )
}

PackageDisplayV2.defaultProps = {
  offer: null,
  compact: false,
  allPosesAreJpg: false,
}

PackageDisplayV2.propTypes = {
  offer: PropTypes.shape({
    code: PropTypes.string,
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  multiPoseHandler: PropTypes.func,
  // Every pose in the session is a baked-background JPG (from OptionSelectionContent).
  allPosesAreJpg: PropTypes.bool,
  packageId: PropTypes.string.isRequired,
  isElementary: PropTypes.bool,
  preorderPose: PropTypes.any,
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    bestValue: PropTypes.bool,
    superValue: PropTypes.bool,
    showDigitalPerks: PropTypes.bool,
  }).isRequired,
  price: PropTypes.shape({
    price: PropTypes.number,
  }),
}

export default PackageDisplayV2
